import { useEffect, useRef } from "react";
import { toast } from "sonner";

/* Native renderer for converted MILTROO themes — NO iframe.
   Injects the converted theme HTML into the React tree, moves stylesheets into
   <head>, re-executes the theme's own scripts (original JS behavior preserved:
   sliders, tabs, modals, hamburger menus all run natively), and wires MILTROO
   route bindings. In "preview" mode, mapped MILTROO actions show their target
   route instead of navigating away; dead links get an explicit warning — never
   a silent no-op, and no action is invented for purely visual buttons. */
export default function ThemeHtmlRenderer({ html, mode = "preview", testid = "theme-html-renderer" }) {
  const ref = useRef(null);

  useEffect(() => {
    const container = ref.current;
    if (!container || !html) return undefined;

    const doc = new DOMParser().parseFromString(html, "text/html");
    const headNodes = [];
    doc.querySelectorAll('link[rel="stylesheet"], style, link[rel="icon"]').forEach((n) => {
      const clone = n.cloneNode(true);
      document.head.appendChild(clone);
      headNodes.push(clone);
      n.remove();
    });

    container.innerHTML = doc.body.innerHTML;

    // Re-execute scripts in document order (inline + external) so the theme's
    // original JavaScript and the MILTROO runtime actually run.
    const runScript = (s) => new Promise((resolve) => {
      const el = document.createElement("script");
      for (const a of s.attributes) el.setAttribute(a.name, a.value);
      el.textContent = s.textContent;
      el.onload = () => resolve();
      el.onerror = () => resolve();
      s.replaceWith(el);
      if (!el.src) resolve();
    });
    (async () => {
      for (const s of Array.from(container.querySelectorAll("script"))) await runScript(s);
    })();

    if (mode === "preview") {
      window.MZ = window.MZ || {};
      window.MZ.previewHook = (p) => toast.success(`Action works — navigates to MILTROO route: ${p || "/"}`, { duration: 2500 });
    }

    // Theme scripts run inside our page: stop THEIR errors (e.g. SSR hydration
    // mismatch in crawled React builds) from bubbling to the outer app's dev
    // overlay / handlers. The theme's own React recovers via client re-render.
    const isThemeErr = (f) => typeof f === "string" && (f.includes("/api/public/imported/") || f.includes("/assets-t/") || f.includes("/api/root/import/"));
    const onError = (e) => { if (isThemeErr(e.filename)) { e.preventDefault(); e.stopImmediatePropagation(); } };
    const onRej = (e) => { e.preventDefault(); };
    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRej, true);

    const onClick = (e) => {
      const a = e.target.closest("a[href], [data-mz-action]");
      if (!a || !container.contains(a)) return;
      if (a.hasAttribute("data-mz-link") || a.hasAttribute("data-mz-action")) return; // runtime handles
      const href = (a.getAttribute("href") || "").trim();
      if (href === "" || href === "#" || href.startsWith("javascript:")) {
        e.preventDefault();
        e.stopPropagation();
        if (mode === "preview") {
          toast.warning("No destination in the original project — left as visual-only (no action invented).", { duration: 2500 });
        }
        return;
      }
      if (href.startsWith("#")) return; // in-page anchor scroll — allowed
      if (mode === "preview" && (href.startsWith("/") || href.endsWith(".html") || !/^[a-z][a-z0-9+.-]*:/i.test(href))) {
        // internal route (incl. React-router links): show the target instead of leaving the admin
        e.preventDefault();
        e.stopPropagation();
        toast.success(`Internal link works — target: ${href}`, { duration: 2500 });
      }
    };
    container.addEventListener("click", onClick, true);

    return () => {
      container.removeEventListener("click", onClick, true);
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRej, true);
      headNodes.forEach((n) => n.remove());
      if (mode === "preview" && window.MZ) delete window.MZ.previewHook;
      container.innerHTML = "";
    };
  }, [html, mode]);

  return <div ref={ref} data-testid={testid} className="imported-theme-root" />;
}
